// src/pages/Services.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaChalkboardTeacher,
  FaPassport,
  FaHeartbeat,
  FaGraduationCap,
  FaUserCheck,
} from "react-icons/fa";
import Seo from "../components/Seo";

const services = [
  {
    title: "Education Consulting",
    path: "/services/education-consulting",
    icon: <FaChalkboardTeacher className="text-3xl md:text-4xl" />,
    blurb:
      "Shortlist the right uni & course based on your goals, budget, and career pathway.",
  },
  {
    title: "Visa Assistance",
    path: "/services/visa-assistance",
    icon: <FaPassport className="text-3xl md:text-4xl" />,
    blurb:
      "GTE/GS review, document checklist, SOP guidance, and end-to-end visa lodgement.",
  },
  {
    title: "Health Insurance (OSHC / OVHC)",
    path: "/services/oshc",
    icon: <FaHeartbeat className="text-3xl md:text-4xl" />,
    blurb:
      "We help arrange the required cover for students, graduates, and visitors.",
  },
  {
    title: "Professional Year Program",
    path: "/services/professional-year",
    icon: <FaGraduationCap className="text-3xl md:text-4xl" />,
    blurb:
      "Boost employability and gain points toward PR with the right PY program.",
  },
  {
    title: "Skills Assessment",
    path: "/services/skills-assessment",
    icon: <FaUserCheck className="text-3xl md:text-4xl" />,
    blurb:
      "Structured support for documents and submissions to assessing authorities.",
  },
];

// Motion variants
const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  }),
};

export default function Services() {
  return (
    <>
      <Seo title="Our Services | Glide Education & Visa Services" />

      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/30 blur-3xl" />
        <div className="absolute -bottom-20 -right-24 h-96 w-96 rounded-full bg-indigo-300/30 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sky-200/25 blur-3xl" />
      </div>

      {/* Hero */}
      <section className="relative bg-gradient-to-b from-white/60 to-blue-50/40">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center">
          <motion.h1
            className="text-4xl md:text-6xl font-extrabold tracking-tight text-blue-800"
            variants={fadeUp}
            initial="hidden"
            animate="show"
          >
            Our Services
          </motion.h1>

          <motion.p
            className="mx-auto mt-4 max-w-2xl text-gray-600 text-lg md:text-xl"
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
          >
            Everything you need for a smooth study and migration journey in Australia.
          </motion.p>

          {/* Animated underline */}
          <motion.div
            className="mx-auto mt-6 h-1 w-24 rounded-full bg-gradient-to-r from-blue-600 to-indigo-500"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            style={{ originX: 0 }}
          />
        </div>
      </section>

      {/* Cards */}
      <section className="relative mx-auto max-w-7xl px-6 pb-6 md:pb-10">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              custom={i}
            >
              <Tilt
                glareEnable
                glareMaxOpacity={0.15}
                glareColor="#60a5fa"
                glarePosition="all"
                className="h-full"
              >
                <Link
                  to={s.path}
                  className="group relative block h-full rounded-2xl border border-white/60 bg-white/70 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.06)] backdrop-blur transition hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)]"
                >
                  {/* corner shimmer */}
                  <span className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-blue-500/10 to-indigo-500/10 blur-2xl transition group-hover:from-blue-500/20 group-hover:to-indigo-500/20" />

                  <div className="flex items-center gap-4">
                    <div className="grid h-14 w-14 place-items-center rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 text-blue-700 ring-1 ring-blue-200 group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:text-white transition-colors">
                      {s.icon}
                    </div>
                    <h3 className="text-lg md:text-xl font-semibold text-blue-900 group-hover:text-blue-700 transition-colors">
                      {s.title}
                    </h3>
                  </div>

                  <p className="mt-3 text-sm md:text-[15px] leading-6 text-gray-600">
                    {s.blurb}
                  </p>

                  <div className="mt-5 flex items-center gap-2 text-blue-700 font-medium">
                    <span>Discover More</span>
                    <motion.span
                      animate={{ x: [0, 6, 0] }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                    >
                      →
                    </motion.span>
                  </div>

                  {/* bottom gradient border */}
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] rounded-b-2xl bg-gradient-to-r from-blue-600 via-indigo-500 to-sky-500 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </Link>
              </Tilt>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA ribbon */}
      <section className="mx-auto max-w-7xl px-6 pb-20">
        <motion.div
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="rounded-2xl bg-white/90 px-6 py-10 md:px-10 md:py-12 backdrop-blur">
            <div className="grid items-center gap-6 md:grid-cols-2">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-blue-900">
                  Not sure where to start?
                </h2>
                <p className="mt-2 text-gray-600">
                  Book a free consultation — we’ll map your study plan, visa route, and timeline.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  Book Consultation
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
}
